import React from "react";
import { ChevronRight, ChevronLeft, CalendarDays } from "lucide-react";
import { damascusToday } from "./format";

export const PERIODS = [
  ["day", "ملخص يومي"],
  ["week", "ملخص أسبوعي"],
  ["quarter", "ملخص ربعي"],
  ["year", "ملخص سنوي"],
];

export const PeriodBar = ({ period, setPeriod, anchor, setAnchor, range, custom, setCustom }) => {
  const isCustom = period === "custom";
  const canNext = !!range && range.next_anchor <= damascusToday();
  return (
    <div className="bg-[#09637E] text-white rounded-2xl p-4 md:p-5 shadow-lg" data-testid="period-bar">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {PERIODS.map(([k, label]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
            className={`h-11 rounded-xl font-extrabold text-sm md:text-base transition-colors ${period === k ? "bg-white text-[#09637E]" : "bg-white/10 hover:bg-white/20 text-white"}`}>
            {label}
          </button>
        ))}
        <button onClick={() => setPeriod("custom")} data-testid="period-custom"
          className={`h-11 rounded-xl font-extrabold text-sm md:text-base flex items-center justify-center gap-1 col-span-2 md:col-span-1 transition-colors ${isCustom ? "bg-white text-[#09637E]" : "bg-white/10 hover:bg-white/20"}`}>
          <CalendarDays size={16} /> فترة مخصصة
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        {isCustom ? (
          <div className="flex flex-wrap items-center gap-2 w-full" data-testid="custom-range">
            <input type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} className="h-11 rounded-lg px-3 text-[#333333] flex-1 min-w-0 max-w-full" data-testid="custom-start" />
            <span className="font-bold shrink-0">إلى</span>
            <input type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} className="h-11 rounded-lg px-3 text-[#333333] flex-1 min-w-0 max-w-full" data-testid="custom-end" />
          </div>
        ) : (
          <>
            <button onClick={() => range && setAnchor(range.prev_anchor)} className="h-11 w-11 shrink-0 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="السابق" data-testid="period-prev">
              <ChevronRight size={22} />
            </button>
            <div className="flex-1 min-w-0 text-center">
              <div className="text-lg md:text-2xl font-extrabold break-words" data-testid="period-label">{range?.label || "…"}</div>
              {period === "day" && (
                <input type="date" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} className="mt-1 h-9 max-w-full rounded-lg px-2 text-[#333333] text-sm" data-testid="period-date" />
              )}
              {period !== "day" && range && <div className="text-xs md:text-sm text-white/80 mt-1 tabular-nums"><bdi dir="ltr">{range.start_date} → {range.end_date}</bdi></div>}
            </div>
            <button onClick={() => canNext && setAnchor(range.next_anchor)} disabled={!canNext} className="h-11 w-11 shrink-0 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30" aria-label="التالي" data-testid="period-next">
              <ChevronLeft size={22} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const Num = ({ children, className = "", testid }) => (
  <bdi dir="auto" className={`tabular-nums ${className}`} data-testid={testid}>{children}</bdi>
);

export const valueSize = (value, big = false) => {
  const len = String(value ?? "").length;
  if (big) return len > 14 ? "text-xl sm:text-3xl md:text-5xl" : "text-2xl sm:text-3xl md:text-5xl";
  if (len > 13) return "text-sm sm:text-xl md:text-3xl";
  if (len > 11) return "text-base sm:text-xl md:text-3xl";
  if (len > 9) return "text-lg sm:text-2xl md:text-3xl";
  return "text-xl sm:text-2xl md:text-3xl";
};

export const Kpi = ({ label, value, sub, tone = "primary", testid, hint, big = false, className = "" }) => {
  const tones = {
    primary: "text-[#09637E]", green: "text-emerald-700", amber: "text-amber-600", red: "text-rose-600", teal: "text-[#088395]", gray: "text-[#555555]",
  };
  return (
    <div className={`bg-white border border-[#DCEBEE] rounded-2xl p-4 md:p-5 shadow-sm flex flex-col justify-between min-h-[108px] min-w-0 max-w-full overflow-hidden ${big ? "col-span-2" : "col-span-1"} ${className}`} data-testid={testid} title={hint || undefined}>
      <div className="text-xs md:text-sm font-bold text-[#666666] leading-tight break-words">{label}</div>
      <div className={`font-extrabold tabular-nums leading-none mt-2 min-w-0 max-w-full break-words ${valueSize(value, big)} ${tones[tone]}`} dir="ltr" data-testid={testid ? `${testid}-value` : undefined}><bdi dir="auto">{value}</bdi></div>
      {sub && <div className="text-[11px] md:text-xs text-[#777777] mt-2 leading-tight break-words tabular-nums">{sub}</div>}
    </div>
  );
};

export const Delta = ({ cur, prev, label }) => {
  if (prev === null || prev === undefined) return null;
  const p = Number(prev);
  if (!p) return <span className="text-[#888888]">{label}: —</span>;
  const d = Math.round(((Number(cur || 0) - p) / p) * 100);
  return <span className={d >= 0 ? "text-emerald-700 font-bold" : "text-rose-600 font-bold"}><bdi dir="ltr">{d >= 0 ? "▲" : "▼"} {Math.abs(d)}%</bdi> <span className="text-[#888888] font-normal">{label}</span></span>;
};

export const Section = ({ title, icon: Icon, children, testid, action }) => (
  <section className="bg-white border border-[#DCEBEE] rounded-2xl p-4 md:p-6 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-4">
      <h2 className="text-base md:text-lg font-extrabold text-[#333333] flex items-center gap-2">{Icon && <Icon className="text-[#09637E]" size={20} />} {title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export const Empty = ({ text, testid }) => <div className="text-center text-[#888888] py-8 text-sm" data-testid={testid}>{text}</div>;

export const IncompleteBadge = ({ count, value }) => (
  <div className="flex flex-wrap items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 text-xs md:text-sm min-w-0" data-testid="profit-incomplete">
    <span className="font-extrabold shrink-0">⚠ حساب الربح غير مكتمل</span>
    <span className="min-w-0 break-words"><Num>{count}</Num> منتج بلا «رأس المال» (مبيعات بقيمة <Num>{value}</Num>). أضف التكلفة من «صور منتجاتي ← تعديل» ليكتمل الحساب. لم يُحتسب أي ربح للمنتجات مجهولة التكلفة.</span>
  </div>
);
